import type { Icon } from "@phosphor-icons/react";
import {
  Archive,
  Brain,
  Compass,
  Folders,
  Gear,
  SquaresFour,
  Storefront,
  UsersThree,
} from "@phosphor-icons/react";
import type { Abilities } from "~/api/types";
import {
  KNOWLEDGE_SECTIONS,
  knowledgeLocation,
} from "~/features/analysis/knowledgeNavigationModel";
import { visibleSettingsGroups } from "~/features/settings/registry";
import { matchStrength, type NavItem, type NavSection, visibleNavSections } from "~/shell/nav";

/**
 * The layered navigation tree (2026-09-09), derived — never declared twice — from the two
 * registries the shell already has: `navSections` (sidebar sections, including the ones the
 * feature registry contributes) and the settings registry (the 28 sections under Admin).
 *
 *   layer 0  sections        Explore · Workspace · Marketplace · People · Storage · Admin
 *   layer 1  groups + areas  Admin › Account › Profile …
 *
 * One catalog feeds the product picker, the sidebar's sections layer, the pinned bar and
 * search, so a destination is the same destination on every surface.
 */
export interface NavDestination {
  /** Stable id: the nav item id, or `settings:<section>` for a settings section. */
  id: string;
  /** Natural-language translation key. */
  title: string;
  icon: Icon;
  to: string;
  sectionId: string;
  /** Translation key of the sub-group inside the section (a settings group). */
  groupTitle?: string;
  keywords?: string;
  /** Further path prefixes the destination owns (see `NavItem.alsoActiveOn`). */
  alsoActiveOn?: string[];
}

export interface NavLayerGroup {
  id: string;
  /** Translation key; absent for the single unnamed group of a flat section. */
  title?: string;
  items: NavDestination[];
}

export interface NavLayerSection {
  id: string;
  title: string;
  description: string;
  icon: Icon;
  groups: NavLayerGroup[];
  count: number;
}

interface SectionMeta {
  title: string;
  description: string;
  icon: Icon;
}

const ADMIN_META: SectionMeta = {
  title: "Admin",
  description: "Account, workspace, automation and governance",
  icon: Gear,
};

/** Display metadata per nav section id. Keys are declared in i18nGuards/dynamicKeys.ts. */
const SECTION_META: Record<string, SectionMeta> = {
  primary: {
    title: "Explore",
    description: "Home, search and conversations",
    icon: Compass,
  },
  work: {
    title: "Work",
    description: "Tiles, to-dos, repositories and live activity",
    icon: SquaresFour,
  },
  workspace: {
    title: "Workspace",
    description: "Collections, documents and drafts",
    icon: Folders,
  },
  marketplace: {
    title: "Marketplace",
    description: "Listings, offers and profiles",
    icon: Storefront,
  },
  people: { title: "People", description: "Members, teams and profiles", icon: UsersThree },
  storage: { title: "Storage", description: "Archive and trash", icon: Archive },
  knowledge: {
    title: "Knowledge",
    description: "Read, explore, research, watch and manage knowledge",
    icon: Brain,
  },
  admin: ADMIN_META,
};

export const SETTINGS_DESTINATION_PREFIX = "settings:";

export function settingsDestinationId(sectionId: string): string {
  return `${SETTINGS_DESTINATION_PREFIX}${sectionId}`;
}

function metaFor(section: NavSection): SectionMeta {
  return (
    SECTION_META[section.id] ?? {
      title: section.title ?? section.id,
      description: "",
      icon: Folders,
    }
  );
}

function fromNavItem(item: NavItem, sectionId: string): NavDestination {
  return {
    id: item.id,
    title: item.title,
    icon: item.icon,
    to: item.to,
    sectionId,
    ...(item.alsoActiveOn ? { alsoActiveOn: item.alsoActiveOn } : {}),
  };
}

/** Layer 0 → layer 1, policy-gated exactly like the sidebar and the settings nav. */
export function navLayerSections(abilities: Abilities): NavLayerSection[] {
  const sections: NavLayerSection[] = [];

  for (const section of visibleNavSections(abilities)) {
    if (section.id === "admin") {
      // Rebuilt below from the settings registry, which knows every section, not just
      // the one "Settings" link the sidebar shows.
      continue;
    }
    const visible = section.items.filter((item) => !item.disabled);
    const items = visible.map((item) => fromNavItem(item, section.id));
    if (items.length === 0) {
      continue;
    }
    sections.push({
      id: section.id,
      ...metaFor(section),
      groups:
        section.id === "knowledge"
          ? KNOWLEDGE_SECTIONS.map((group) => ({
              id: `knowledge-${group.id}`,
              title: group.label,
              items: visible
                .filter((item) => knowledgeLocation(item.to)?.section.id === group.id)
                .map((item) => ({
                  ...fromNavItem(item, section.id),
                  groupTitle: group.label,
                })),
            })).filter((group) => group.items.length > 0)
          : visible.some((item) => item.secondary)
            ? [
                {
                  id: section.id,
                  items: visible
                    .filter((item) => !item.secondary)
                    .map((item) => fromNavItem(item, section.id)),
                },
                {
                  id: `${section.id}-more`,
                  title: "More knowledge views",
                  items: visible
                    .filter((item) => item.secondary)
                    .map((item) => ({
                      ...fromNavItem(item, section.id),
                      groupTitle: "More knowledge views",
                    })),
                },
              ]
            : [{ id: section.id, items }],
      count: items.length,
    });
  }

  const groups: NavLayerGroup[] = visibleSettingsGroups(abilities).map((group) => ({
    id: `settings-${group.id}`,
    title: group.title,
    items: group.sections.map((section) => ({
      id: settingsDestinationId(section.id),
      title: section.title,
      icon: section.icon,
      to: `/settings/${section.id}`,
      sectionId: "admin",
      groupTitle: group.title,
      ...(section.keywords ? { keywords: section.keywords } : {}),
    })),
  }));
  const count = groups.reduce((total, group) => total + group.items.length, 0);
  if (count > 0) {
    sections.push({ id: "admin", ...ADMIN_META, groups, count });
  }

  return sections;
}

/** Every destination, flat. */
export function navCatalog(abilities: Abilities): NavDestination[] {
  return flatten(navLayerSections(abilities));
}

export function flatten(sections: NavLayerSection[]): NavDestination[] {
  return sections.flatMap((section) => section.groups.flatMap((group) => group.items));
}

/** The destination the pathname belongs to — the most specific match wins. */
export function activeDestination(
  catalog: NavDestination[],
  pathname: string,
): NavDestination | undefined {
  let best: NavDestination | undefined;
  let bestStrength = -1;
  for (const destination of catalog) {
    const strength = matchStrength(destination, pathname);
    if (strength > bestStrength) {
      best = destination;
      bestStrength = strength;
    }
  }
  return best;
}

/** Which section the current route lives in — what the picker button and the sidebar title show. */
export function resolveNavSection(
  sections: NavLayerSection[],
  pathname: string,
): NavLayerSection | undefined {
  const active = activeDestination(flatten(sections), pathname);
  if (active) {
    return sections.find((section) => section.id === active.sectionId);
  }
  if (pathname.startsWith("/settings")) {
    return sections.find((section) => section.id === "admin");
  }
  return sections.find((section) => section.id === "primary") ?? sections[0];
}

export interface NavHit {
  destination: NavDestination;
  /** "Admin › Workspace" — already translated. */
  crumb: string;
  score: number;
}

type Translate = (key: string) => string;

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase();
}

/**
 * Search every layer at once. Matching runs on the *translated* titles so a Hebrew viewer
 * finds "חברים" and an English one "Members"; the untranslated key, the crumb, the keywords
 * and the path words are secondary evidence. Ranking: title prefix > title substring >
 * secondary. Every term must match somewhere.
 */
export function searchNav(sections: NavLayerSection[], query: string, t: Translate): NavHit[] {
  const q = normalize(query);
  if (!q) {
    return [];
  }
  const terms = q.split(/\s+/).filter(Boolean);
  const hits: NavHit[] = [];

  for (const section of sections) {
    const sectionTitle = t(section.title);
    for (const group of section.groups) {
      const groupTitle = group.title ? t(group.title) : undefined;
      const crumb = groupTitle ? `${sectionTitle} › ${groupTitle}` : sectionTitle;
      for (const destination of group.items) {
        const title = normalize(t(destination.title));
        const secondary = normalize(
          [
            crumb,
            destination.title,
            destination.keywords ?? "",
            destination.to.replace(/[/-]/g, " "),
          ]
            .join(" ")
            .replace(/›/g, " "),
        );
        let score = 0;
        for (const term of terms) {
          if (title.startsWith(term)) {
            score += 100;
          } else if (title.includes(term)) {
            score += 60;
          } else if (secondary.includes(term)) {
            score += 20;
          } else {
            score = 0;
            break;
          }
        }
        if (score > 0) {
          hits.push({ destination, crumb, score });
        }
      }
    }
  }

  return hits.sort(
    (a, b) => b.score - a.score || t(a.destination.title).localeCompare(t(b.destination.title)),
  );
}
